<template>
    <div
        data-dialog="true"
        id="panel"
        class="mx-auto border border-gray-225 dark:border-gray-850 absolute -left-8 -right-8 top-1 h-auto w-[935px] rounded-md bg-gray-tier-3 panel-shadow dark:bg-v2-gray-40"
    >
        <!-- Search Input Section -->
        <div 
            :class="isAiActive ? 'px-6 py-6' : 'px-6 py-6 border-b border-keyline-primary'"
        >
            <SearchInputSection
                v-model="searchTerm"
                :show-skeleton="showSkeleton"
                :show-results="showResults"
                @close="forceClose"
                @clear="handleSearchClear"
                @search="handleSearch"
                ref="searchInput"
            />

            <!-- AI Assistant Component -->
            <AiAssistant
                :search-term="searchTerm"
                :should-show-ai-button="false"
                @ai-started="handleAiStarted"
                @ai-completed="handleAiCompleted"
                ref="aiAssistant"
            />
        </div>

        <!-- Loading State -->
        <div v-if="isLoadingMeta">
            <!-- Recent Searches Skeleton -->
            <div class="border-b border-v2-gray-110 px-8 py-6 dark:border-v2-gray-10">
                <h5 class="mb-2 font-medium type-tertiary text-sm">{{ $t("Recently searched") }}</h5>
                <ul class="flex flex-col opacity-50 pointer-events-none">
                    <li v-for="n in 5" :key="n" class="py-1.5 inline-flex items-center">
                        <div class="loading-value h-6 w-5 mr-2 rounded"></div>
                        <div class="loading-value h-4 rounded" :class="n === 1 ? 'w-32' : n === 2 ? 'w-24' : n === 3 ? 'w-40' : 'w-28'"></div>
                    </li>
                </ul>
            </div>

            <!-- Recently Viewed Skeleton -->
            <div>
                <h5 class="mt-6 px-8 mb-4 font-medium type-tertiary text-sm">{{ $t("Recently viewed") }}</h5>
                <ul class="pb-8 px-8 w-full overflow-x-auto flex gap-2 first-of-type:-pl-2 opacity-50 pointer-events-none">
                    <li class="shrink-0 w-[167px]" v-for="n in 5" :key="n">
                        <div class="bg-v2-gray-130 flex flex-col p-4 rounded h-full dark:bg-v2-gray-50">
                            <!-- Product Image Skeleton -->
                            <div class="flex justify-center flex-col h-20 mb-1">
                                <div class="loading-value h-16 w-16 mx-auto rounded"></div>
                            </div>
                            <!-- Product SKU Skeleton -->
                            <div class="loading-value h-4 mx-auto rounded" :class="n % 2 === 0 ? 'w-20' : 'w-16'"></div>
                        </div>
                    </li>
                </ul>
            </div>
        </div>

        <!-- Main Content - Hidden when AI is active -->
        <div
            v-else-if="!isAiActive"
        >
            <!-- Recent Searches -->
            <RecentSearches
                v-if="!searchTerm.length"
                :recent-searches="useSettingsLoader().settings('recently_queried')"
                :is-loading="isLoadingMeta"
                @search-selected="handleSearchSelected"
            />

            <!-- Recently Viewed -->
            <RecentlyViewed
                v-if="!searchTerm.length"
                :recently-viewed="useSettingsLoader().settings('recently_viewed')"
                :is-loading="isLoadingMeta"
            />

            <!-- Search Results -->
            <SourcingSearchResults
                :search-term="searchTerm"
                :search-results-term="searchResultsTerm"
                :search-results="searchResults"
                :search-suggestions="searchSuggestions"
                :is-loading="searchIsLoading"
                :show-skeleton="showSkeleton"
                :show-results="showResults"
                @suggestion-clicked="handleSuggestionClicked"
                @result-clicked="searchTrackClick"
                ref="searchResultsComponent"
            />

            <!-- No Results -->
            <NoResults
                :search-term="searchTerm"
                :search-results-term="searchResultsTerm"
                :has-results="searchHasResults"
                :is-loading="searchIsLoading"
            />
        </div>

        <div class="px-8 py-4 flex gap-2 items-center justify-between text-sm type-tertiary leading-none border-t border-keyline-primary">
            <div class="hidden at1200:block">
                {{ $t("Press the") }}
                <span class="text-center p-1 mx-0.5 shadow-2xl rounded border border-gray-tier-7 border-b-2 inline-block">S</span>
                {{ $t("key to access search faster") }}
            </div>
            <div class="flex items-center justify-between gap-2">
                <span class="inline-block">{{ $t("Not the results you expected?") }}</span>
                <button @click="showNewMessage" class="text-left type-primary underline decoration-dotted decoration-1 hover:decoration-solid underline-offset-4 inline-block font-medium">
                    {{ $t("Chat with us") }}
                </button>
            </div>
        </div>

    </div>
</template>

<script setup>
import {inject, onMounted, ref, watch, computed, nextTick} from "vue";
import {
    eventBus,
    useBasicSearchApi, useChat,
    useDialog,
    useSettingsLoader,
} from "@/Composables";
import SearchInputSection from "./SourcingSearchDialog/SearchInputSection.vue";
import AiAssistant from "./SourcingSearchDialog/AiAssistant.vue";
import RecentSearches from "./SourcingSearchDialog/RecentSearches.vue";
import RecentlyViewed from "./SourcingSearchDialog/RecentlyViewed.vue";
import SourcingSearchResults from "./SourcingSearchDialog/SourcingSearchResults.vue";
import NoResults from "./SourcingSearchDialog/NoResults.vue";

const route = inject("route");
const { forceClose } = useDialog();
const {
    searchTerm,
    searchIsLoading,
    searchResults,
    searchHasResults,
    searchWasSearched,
    searchSuggestions,
    searchTrackClick,
    searchResultsTerm,
    searchTermQuick,
} = useBasicSearchApi();

const { showNewMessage } = useChat();

// Component refs
const searchInput = ref(null);
const aiAssistant = ref(null);
const searchResultsComponent = ref(null);

// AI State Management
const isAiActive = computed(() => {
    return aiAssistant.value?.isAiActive || false;
});

// Determine when to show AI button (dummy logic - replace with real logic)
const shouldShowAiButton = computed(() => {
    // Show AI button if search term looks like a question (contains question words or ?)
    const questionWords = ['does','how', 'what', 'where', 'when', 'why', 'which', 'can', 'should', 'install', 'adjust', 'fix', 'troubleshoot', '?'];
    const searchLower = searchTerm.value.toLowerCase();
    return searchTerm.value.length > 10 && questionWords.some(word => searchLower.includes(word));
});

const isLoadingMeta = ref(true);
const isSearchCleared = ref(false);

// Event handlers
const handleSearchClear = () => {
    // Reset AI state when search is cleared
    aiAssistant.value?.resetAiState();
    // Mark search as cleared to hide skeleton
    isSearchCleared.value = true;
};

const handleSearch = (term) => {
    // Handle explicit search from input
    console.log('Searching for:', term);
};

const handleSearchSelected = (query) => {  
    // Reset cleared state to allow skeleton to show during new search
    isSearchCleared.value = false;
    searchTermQuick.value = query;
};

const handleSuggestionClicked = (term) => {
    eventBus.emit('search:suggestion-clicked', term);
};

const handleAiStarted = () => {
    console.log('AI processing started');
};

const handleAiCompleted = (results) => {
    console.log('AI processing completed with results:', results);
};

const showSkeleton = computed(() => {
    // Show skeleton when:
    // 1. Actually loading OR
    // 2. Search term doesn't match results term (waiting for results to update) AND we have a search term AND not cleared
    const isActuallyLoading = isLoadingMeta.value || searchIsLoading.value;
    const isWaitingForMatchingResults = searchTerm.value.length > 0 && searchTerm.value !== searchResultsTerm.value && !isSearchCleared.value;
    const result = (isActuallyLoading || isWaitingForMatchingResults) && !isSearchCleared.value;
    return result;
});

const showResults = computed(() => {
    // Only show results if:
    // 1. We have a search term
    // 2. We have results
    // 3. We're not loading
    // 4. We're not cleared
    // 5. The current search term matches the results term (results are for current search)
    const resultsMatchCurrentSearch = searchTerm.value === searchResultsTerm.value;
    const result = searchTerm.value.length && searchHasResults.value && !searchIsLoading.value && !isSearchCleared.value && resultsMatchCurrentSearch;
    return result;
});

// Reset AI state when search term changes significantly
watch(searchTerm, (newValue, oldValue) => {
    if (oldValue && newValue !== oldValue) {
        aiAssistant.value?.resetAiState();
    }
    if (newValue === '') {
        searchInput.value?.focus();
    }
    // Reset cleared state when user starts typing again
    if (newValue.length > 0 && isSearchCleared.value) {
        isSearchCleared.value = false;
    }
});

// Reset scroll position when search changes
watch(searchIsLoading, () => {
    if (searchResultsComponent.value) {
        searchResultsComponent.value.scrollToTop();
    }
});

// Refocus input when loading completes
watch(isLoadingMeta, (newValue, oldValue) => {
    // When loading finishes (goes from true to false), refocus the input
    if (oldValue === true && newValue === false) {
        nextTick(() => {
            searchInput.value?.focus();
        });
    }
});

onMounted(()=>{
    useSettingsLoader().fetchSettings(['recently_queried','recently_viewed']).finally(()=> isLoadingMeta.value = false);
});
</script>
